import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  InputNumber,
  message,
  Progress,
  Row,
  Space,
  Spin,
  Typography,
  Upload,
  Alert,
} from 'antd';
import {
  DownloadOutlined,
  MergeCellsOutlined,
  PlayCircleOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import axios from './auth';

const { Title, Text, Paragraph } = Typography;
const API_BASE = '/api';

function Merge() {
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const [loadingVideo, setLoadingVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [sourceVideo, setSourceVideo] = useState(null);
  const [resultVideo, setResultVideo] = useState(null);
  const nickname = localStorage.getItem('nickname') || localStorage.getItem('username') || '用户';

  const loadHistoryVideo = async (historyId) => {
    if (!historyId) {
      setSourceVideo(null);
      return;
    }
    setLoadingVideo(true);
    try {
      const res = await axios.get(`${API_BASE}/history/gen2/${historyId}`);
      if (res.data.success) {
        setSourceVideo(res.data.data);
      } else {
        setSourceVideo(null);
        message.error(res.data.error || '加载视频失败');
      }
    } catch (err) {
      setSourceVideo(null);
      message.error(err.response?.data?.error || '加载视频失败，请检查视频编号');
    } finally {
      setLoadingVideo(false);
    }
  };

  useEffect(() => {
    const initialHistoryId = searchParams.get('historyId') || searchParams.get('videoId');
    if (initialHistoryId) {
      const parsed = Number(initialHistoryId);
      if (Number.isFinite(parsed) && parsed > 0) {
        form.setFieldsValue({ historyId: parsed });
        loadHistoryVideo(parsed);
      }
    }
  }, [form, searchParams]);

  const selectedHistoryId = Form.useWatch('historyId', form);

  const handleLookupVideo = async () => {
    const historyId = form.getFieldValue('historyId');
    if (!historyId) {
      message.warning('请输入历史记录里的视频编号');
      return;
    }
    await loadHistoryVideo(historyId);
  };

  const handleSubmit = async (values) => {
    if (!values.historyId) {
      message.warning('请输入历史记录里的视频编号');
      return;
    }

    const formData = new FormData();
    formData.append('historyId', values.historyId);
    formData.append('repeatCount', values.repeatCount || 1);
    if (values.music?.fileList?.[0]?.originFileObj) {
      formData.append('music', values.music.fileList[0].originFileObj);
    }

    setSubmitting(true);
    setProgress(15);
    setResultVideo(null);
    const timer = setInterval(() => {
      setProgress(prev => (prev >= 90 ? prev : prev + 5));
    }, 1200);

    try {
      const res = await axios.post(`${API_BASE}/merge/create`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        setProgress(100);
        setResultVideo(res.data.file);
        message.success('视频合并成功');
        await loadHistoryVideo(values.historyId);
      } else {
        message.error(res.data.error || '视频合并失败');
      }
    } catch (err) {
      message.error(err.response?.data?.error || '视频合并失败');
    } finally {
      clearInterval(timer);
      setSubmitting(false);
      setTimeout(() => setProgress(0), 1200);
    }
  };

  const uploadProps = {
    accept: 'audio/*,.mp3,.wav,.aac,.m4a,.flac,.ogg',
    maxCount: 1,
    beforeUpload: () => false,
  };

  return (
    <div className="page-fade">
      <div className="page-header">
        <div>
          <Title level={2}>视频合并</Title>
          <Paragraph type="secondary">
            输入“历史记录”里的试穿视频编号，将该视频重复拼接成更长视频，可叠加背景音乐，并自动添加动态防盗水印：@{nickname}
          </Paragraph>
        </div>
      </div>

      <Row gutter={[20, 20]}>
        <Col xs={24} lg={10}>
          <Card title="合并参数" bordered={false}>
            <Form
              form={form}
              layout="vertical"
              initialValues={{ repeatCount: 1 }}
              onFinish={handleSubmit}
            >
              <Form.Item
                name="historyId"
                label="输入要处理的视频编号"
                tooltip="这里填写 Gen2 历史记录里的编号，例如 27。也可以从商品页面关联视频的“合并视频”按钮自动带入。"
                rules={[{ required: true, message: '请输入视频编号' }]}
              >
                <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder="例如：27" />
              </Form.Item>

              <Button
                icon={<SearchOutlined />}
                onClick={handleLookupVideo}
                loading={loadingVideo}
                disabled={!selectedHistoryId || submitting}
                style={{ marginBottom: 16 }}
                block
              >
                查询并预览该视频
              </Button>

              <Form.Item
                name="repeatCount"
                label="重复拼接次数"
                tooltip="例如填 3：同一个视频连续播放 3 遍，生成一个更长的新视频。最大 20 次。"
                rules={[{ required: true, message: '请输入重复次数' }]}
              >
                <InputNumber min={1} max={20} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="music"
                label="背景音乐（可选）"
                valuePropName="file"
                getValueFromEvent={e => e}
              >
                <Upload {...uploadProps}>
                  <Button icon={<UploadOutlined />}>选择音乐文件</Button>
                </Upload>
              </Form.Item>

              <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
                <Space direction="vertical" size={4}>
                  <Text strong>动态水印</Text>
                  <Text type="secondary">内容：@{nickname}</Text>
                  <Text type="secondary">轨迹：右上角 → 居中 → 右下角</Text>
                </Space>
              </Card>

              <Alert
                type="info"
                showIcon
                message="说明"
                description="视频编号就是 Gen2 页面历史记录卡片标题里的编号，例如“视频 27”。本页面处理的是生成好的试穿视频，不是动作参考视频。"
                style={{ marginBottom: 16 }}
              />

              {submitting && (
                <div style={{ marginBottom: 16 }}>
                  <Progress percent={progress} status={progress >= 100 ? 'success' : 'active'} />
                  <Text type="secondary">ffmpeg 正在处理，请不要关闭页面...</Text>
                </div>
              )}

              <Button
                type="primary"
                htmlType="submit"
                icon={<MergeCellsOutlined />}
                loading={submitting}
                block
                size="large"
              >
                开始合并生成
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Card title="预览 / 结果" bordered={false}>
            <Spin spinning={loadingVideo}>
              {sourceVideo ? (
                <div style={{ marginBottom: 24 }}>
                  <Space direction="vertical" size={4} style={{ marginBottom: 12 }}>
                    <Text strong>原试穿视频预览（编号：{sourceVideo.id}）</Text>
                    <Text type="secondary">生成时间：{sourceVideo.createdAt ? new Date(sourceVideo.createdAt).toLocaleString('zh-CN') : '-'}</Text>
                  </Space>
                  <video
                    src={sourceVideo.videoUrl}
                    controls
                    style={{ width: '100%', maxHeight: 360, marginTop: 12, borderRadius: 12, background: '#000' }}
                  />
                </div>
              ) : (
                <Empty description="请输入历史记录里的视频编号并查询预览" style={{ margin: '32px 0' }} />
              )}

              {resultVideo && (
                <div>
                  <Text strong>新生成视频</Text>
                  <video
                    src={resultVideo.url}
                    controls
                    style={{ width: '100%', maxHeight: 420, marginTop: 12, borderRadius: 12, background: '#000' }}
                  />
                  <Space style={{ marginTop: 12 }}>
                    <Button icon={<PlayCircleOutlined />} href={resultVideo.url} target="_blank">打开视频</Button>
                    <Button icon={<DownloadOutlined />} href={resultVideo.url} download>下载视频</Button>
                  </Space>
                </div>
              )}
            </Spin>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Merge;
